import { useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import Tabs from "@/components/base/Tabs";
import StatCard from "@/components/base/StatCard";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import { getActiveSchoolYear, getCycles, getSchoolInfo, getSchoolYears } from "@/services/schoolService";
import { getClasses } from "@/services/academicService";
import { getEnrollments, getGuardians, getStudentGuardians, getStudents } from "@/services/studentService";
import { cancelReceipt, getBalancesForYears, getReceipts, PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import { formatDate, formatMoney } from "@/utils/format";
import {
  buildEnrollmentReceipt,
  buildVersementReceipt,
  toFeeSnapshot,
  type FeeSnapshot,
  type ReceiptData,
  type ReceiptStudent,
} from "@/utils/receipt";
import type { Cycle, Enrollment, EnrollmentBalance, Guardian, Receipt, SchoolClass, Student } from "@/types";
import EncaissementModal, { type EnrollmentOption } from "./components/EncaissementModal";
import ReceiptPrintModal from "./components/ReceiptPrintModal";
import StudentPaymentsModal from "./components/StudentPaymentsModal";

async function load() {
  const [students, guardians, links, enrollments, classes, cycles, schoolYears, receipts, school] = await Promise.all([
    getStudents(),
    getGuardians(),
    getStudentGuardians(),
    getEnrollments(),
    getClasses(),
    getCycles(),
    getSchoolYears(),
    getReceipts(),
    getSchoolInfo(),
  ]);
  // Situation de chaque inscription, année par année (calculée par le serveur depuis la fiche).
  const balances = await getBalancesForYears(schoolYears.map((y) => y.label));
  return { students, guardians, links, enrollments, classes, cycles, schoolYears, balances, receipts, school };
}

interface Data {
  students: Student[];
  guardians: Guardian[];
  links: { studentId: string; guardianId: string; isPrimary: boolean }[];
  enrollments: Enrollment[];
  classes: SchoolClass[];
  cycles: Cycle[];
  schoolYears: { id: string; label: string; active: boolean }[];
  balances: EnrollmentBalance[];
  receipts: Receipt[];
  school: Awaited<ReturnType<typeof getSchoolInfo>>;
}

export default function PaymentsPage() {
  const activeYear = getActiveSchoolYear();
  const { allowedCycleIds, can } = useAuth();
  const { notify } = useToast();
  const { data, loading, error, reload } = useAsync(load, []);
  const [yearFilter, setYearFilter] = useState(activeYear);
  const [cycleFilter, setCycleFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("recouvrement");

  const [encaissementOpen, setEncaissementOpen] = useState(false);
  const [presetEnrollmentId, setPresetEnrollmentId] = useState<string | undefined>(undefined);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  const [toCancel, setToCancel] = useState<Receipt | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const pagerEnrollments = usePager();
  const pagerReceipts = usePager();

  const derived = useMemo(() => {
    if (!data) return null;
    const { students, enrollments, classes, cycles, balances, receipts } = data as Data;
    const studentById = new Map(students.map((s) => [s.id, s]));
    const classById = new Map(classes.map((c) => [c.id, c]));
    const cycleById = new Map(cycles.map((c) => [c.id, c]));
    const enrollmentById = new Map(enrollments.map((e) => [e.id, e]));

    const balanceById = new Map(balances.map((b) => [b.enrollmentId, b]));
    const linesOf = (enrId: string) => balanceById.get(enrId)?.lines ?? [];
    const feesOf = (enrId: string): FeeSnapshot[] => linesOf(enrId).map(toFeeSnapshot);

    const className = (id: string) => classById.get(id)?.name ?? "—";
    const cycleLabel = (id: string) => cycleById.get(id)?.label ?? "—";
    const receiptStudentOf = (enr: Enrollment, student: Student): ReceiptStudent => ({
      name: `${student.firstName} ${student.lastName}`,
      matricule: student.matricule,
      className: className(enr.classId),
      cycleLabel: cycleLabel(enr.cycleId),
      schoolYear: enr.schoolYear,
    });

    const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
    const visibleClasses = classes.filter((c) => allowedCycleIds.includes(c.cycleId));

    const matchSearch = (studentId: string) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const s = studentById.get(studentId);
      if (!s) return false;
      return (
        s.lastName.toLowerCase().includes(q) ||
        s.firstName.toLowerCase().includes(q) ||
        s.matricule.toLowerCase().includes(q)
      );
    };

    const filteredEnrollments = enrollments
      .filter((e) => allowedCycleIds.includes(e.cycleId))
      .filter((e) => yearFilter === "all" || e.schoolYear === yearFilter)
      .filter((e) => cycleFilter === "all" || e.cycleId === cycleFilter)
      .filter((e) => classFilter === "all" || e.classId === classFilter)
      .filter((e) => matchSearch(e.studentId))
      .sort((a, b) => {
        const sa = studentById.get(a.studentId);
        const sb = studentById.get(b.studentId);
        return `${sa?.lastName ?? ""}`.localeCompare(`${sb?.lastName ?? ""}`);
      });

    const filteredVersements = receipts
      .filter((v) => {
        const enr = enrollmentById.get(v.enrollmentId);
        if (!enr || !allowedCycleIds.includes(enr.cycleId)) return false;
        if (yearFilter !== "all" && v.schoolYear !== yearFilter) return false;
        if (cycleFilter !== "all" && enr.cycleId !== cycleFilter) return false;
        if (classFilter !== "all" && enr.classId !== classFilter) return false;
        return matchSearch(v.studentId);
      })
      .sort((a, b) => b.date.localeCompare(a.date) || b.receiptNo.localeCompare(a.receiptNo));

    const options: EnrollmentOption[] = filteredEnrollments
      .map((e) => {
        const s = studentById.get(e.studentId);
        if (!s) return null;
        return {
          id: e.id,
          studentId: e.studentId,
          studentName: `${s.firstName} ${s.lastName}`,
          matricule: s.matricule,
          className: className(e.classId),
          cycleLabel: cycleLabel(e.cycleId),
          schoolYear: e.schoolYear,
          lines: linesOf(e.id),
        } as EnrollmentOption;
      })
      .filter((o): o is EnrollmentOption => !!o && o.lines.some((l) => l.remaining > 0));

    return {
      studentById,
      classById,
      enrollmentById,
      feesOf,
      cycleLabel,
      receiptStudentOf,
      visibleCycles,
      visibleClasses,
      filteredEnrollments,
      filteredVersements,
      options,
    };
  }, [data, allowedCycleIds, yearFilter, cycleFilter, classFilter, search]);

  if (loading && !data) return <LoadingState label="Chargement des paiements..." />;
  if (error || !data || !derived) {
    return <ErrorState message={error ?? "Données indisponibles."} onRetry={reload} />;
  }

  const { school, schoolYears } = data as Data;
  const { feesOf, receiptStudentOf, filteredEnrollments, filteredVersements } = derived;

  const totals = filteredEnrollments.reduce(
    (acc, e) => {
      const fees = feesOf(e.id);
      acc.due += fees.reduce((s, f) => s + f.due, 0);
      acc.paid += fees.reduce((s, f) => s + f.paid, 0);
      return acc;
    },
    { due: 0, paid: 0 },
  );
  const remainingTotal = Math.max(0, totals.due - totals.paid);

  const validVersements = filteredVersements.filter((v) => !v.cancelledAt);
  const matchedVersementTotal = validVersements.reduce((s, v) => s + v.total, 0);
  const filterKey = `${yearFilter}|${cycleFilter}|${classFilter}|${search}`;
  const enrollmentsPage = pagerEnrollments.slice(filteredEnrollments, filterKey);
  const receiptsPage = pagerReceipts.slice(filteredVersements, filterKey);

  const openEncaissement = (enrollmentId?: string) => {
    setPresetEnrollmentId(enrollmentId);
    setEncaissementOpen(true);
  };

  const openVersementReceipt = (v: Receipt) => {
    const enr = derived.enrollmentById.get(v.enrollmentId);
    const student = derived.studentById.get(v.studentId);
    if (!enr || !student) return;
    setReceiptData(
      buildVersementReceipt({ receipt: v, fees: feesOf(v.enrollmentId), student: receiptStudentOf(enr, student) }),
    );
  };

  const openEnrollmentReceipt = (enr: Enrollment) => {
    const student = derived.studentById.get(enr.studentId);
    if (!student) return;
    setReceiptData(
      buildEnrollmentReceipt({
        fees: feesOf(enr.id),
        receipts: data.receipts.filter((v) => v.enrollmentId === enr.id),
        student: receiptStudentOf(enr, student),
        title: "Situation des paiements",
      }),
    );
  };

  const confirmCancel = async () => {
    if (!toCancel) return;
    if (!cancelReason.trim()) {
      notify("Indiquez le motif de l'annulation.", "error");
      return;
    }
    setCancelling(true);
    try {
      await cancelReceipt(toCancel.id, cancelReason);
      notify(`Reçu ${toCancel.receiptNo} annulé.`, "info");
      setToCancel(null);
      setCancelReason("");
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Annulation impossible.", "error");
    } finally {
      setCancelling(false);
    }
  };

  const detailEnr = detailId ? derived.enrollmentById.get(detailId) ?? null : null;
  const detailStudent = detailEnr ? derived.studentById.get(detailEnr.studentId) ?? null : null;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Paiements & reçus"
        subtitle="Encaissement des tranches, reçus imprimables et historique complet des versements."
        icon="ri-hand-coin-line"
        actions={
          can("payments:create") ? (
            <Button icon="ri-add-line" onClick={() => openEncaissement()}>
              Nouvel encaissement
            </Button>
          ) : null
        }
      />

      <CycleScopeBanner className="mb-5" />

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total attendu" value={formatMoney(totals.due)} icon="ri-file-list-3-line" tone="secondary" />
        <StatCard label="Total encaissé" value={formatMoney(totals.paid)} icon="ri-money-dollar-circle-line" tone="primary" />
        <StatCard label="Reste à recouvrer" value={formatMoney(remainingTotal)} icon="ri-error-warning-line" tone="accent" />
        <StatCard
          label="Reçus (filtres)"
          value={String(validVersements.length)}
          icon="ri-receipt-line"
          tone="secondary"
          hint={`${formatMoney(matchedVersementTotal)} encaissés`}
        />
      </div>

      <div className="mb-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
          >
            <option value="all">Toutes les années</option>
            {schoolYears.map((y) => (
              <option key={y.id} value={y.label}>
                {y.label}
                {y.active ? " (active)" : ""}
              </option>
            ))}
          </select>
          <select
            value={cycleFilter}
            onChange={(e) => setCycleFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
          >
            <option value="all">Tous les cycles</option>
            {derived.visibleCycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
          >
            <option value="all">Toutes les classes</option>
            {derived.visibleClasses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, prénom ou matricule..."
              className="w-full sm:w-52 bg-transparent text-sm focus:outline-none"
            />
          </div>
        </div>
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { id: "recouvrement", label: "Recouvrement", icon: "ri-list-check-2", count: filteredEnrollments.length },
            { id: "journal", label: "Journal des reçus", icon: "ri-history-line", count: filteredVersements.length },
          ]}
        />
      </div>

      {tab === "recouvrement" ? (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-medium">Élève</th>
                  <th className="px-4 py-3 font-medium">Classe</th>
                  <th className="px-4 py-3 text-right font-medium">Total dû</th>
                  <th className="px-4 py-3 text-right font-medium">Payé</th>
                  <th className="px-4 py-3 text-right font-medium">Reste</th>
                  <th className="px-4 py-3 font-medium">Progression</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrollmentsPage.items.map((e) => {
                  const s = derived.studentById.get(e.studentId);
                  if (!s) return null;
                  const fees = feesOf(e.id);
                  const due = fees.reduce((sum, f) => sum + f.due, 0);
                  const paid = fees.reduce((sum, f) => sum + f.paid, 0);
                  const rest = Math.max(0, due - paid);
                  const pct = due > 0 ? Math.min(100, Math.round((paid / due) * 100)) : 0;
                  return (
                    <tr
                      key={e.id}
                      className="border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar lastName={s.lastName} firstName={s.firstName} src={s.photo || undefined} size="sm" />
                          <div>
                            <p className="font-medium text-foreground-900">
                              {s.firstName} {s.lastName}
                            </p>
                            <p className="text-xs text-foreground-500">
                              {s.matricule} • {e.schoolYear}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-foreground-800">{derived.classById.get(e.classId)?.name ?? "—"}</p>
                        <p className="text-xs text-foreground-500">
                          {derived.cycleLabel(e.cycleId)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-foreground-700">{formatMoney(due)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-primary-700">{formatMoney(paid)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {rest > 0 ? (
                          <span className="font-medium text-accent-700">{formatMoney(rest)}</span>
                        ) : (
                          <Badge tone="success">Soldé</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-background-200">
                            <div className="h-full rounded-full bg-primary-600" style={{ width: `${pct}%` }}></div>
                          </div>
                          <span className="text-xs text-foreground-500">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailId(e.id)}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-history-line"></i>
                            Détail
                          </button>
                          <button
                            onClick={() => openEnrollmentReceipt(e)}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-file-text-line"></i>
                            Reçu
                          </button>
                          {can("payments:create") && (
                          <button
                            onClick={() => openEncaissement(e.id)}
                            disabled={rest <= 0}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs text-primary-800 hover:bg-primary-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <i className="ri-hand-coin-line"></i>
                            Encaisser
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredEnrollments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-foreground-400">
                      Aucune inscription pour ces filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination pager={enrollmentsPage} className="border-t border-background-200" />
        </Card>
      ) : (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] text-sm">
              <thead>
                <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">N° reçu</th>
                  <th className="px-4 py-3 font-medium">Élève</th>
                  <th className="px-4 py-3 font-medium">Classe</th>
                  <th className="px-4 py-3 font-medium">Frais</th>
                  <th className="px-4 py-3 font-medium">Mode</th>
                  <th className="px-4 py-3 text-right font-medium">Montant</th>
                  <th className="px-4 py-3 font-medium">Encaissé par</th>
                  <th className="px-4 py-3 text-right font-medium">Reçu</th>
                </tr>
              </thead>
              <tbody>
                {receiptsPage.items.map((v) => {
                  const enr = derived.enrollmentById.get(v.enrollmentId);
                  const s = derived.studentById.get(v.studentId);
                  return (
                    <tr
                      key={v.id}
                      className={`border-b border-background-200 last:border-0 hover:bg-background-100/60 transition-colors ${v.cancelledAt ? "opacity-60" : ""}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-foreground-600">{formatDate(v.date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-foreground-900">
                        <span className={v.cancelledAt ? "line-through" : ""}>{v.receiptNo}</span>
                        {v.cancelledAt && (
                          <span className="ml-2 align-middle">
                            <Badge tone="accent">Annulé</Badge>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-foreground-800">{s ? `${s.firstName} ${s.lastName}` : "—"}</td>
                      <td className="px-4 py-3 text-foreground-700">{enr ? derived.classById.get(enr.classId)?.name ?? "—" : "—"}</td>
                      <td className="px-4 py-3 text-foreground-800" title={v.cancelledAt ? `Annulé par ${v.cancelledBy} : ${v.cancelReason}` : undefined}>
                        {v.lines.map((l) => l.label).join(", ")}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone="secondary">{PAYMENT_METHOD_LABEL[v.method]}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-foreground-900">{formatMoney(v.total)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground-600">{v.recordedBy}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openVersementReceipt(v)}
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-printer-line"></i>
                          Reçu
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredVersements.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-foreground-400">
                      Aucun versement pour ces filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination pager={receiptsPage} className="border-t border-background-200" />
        </Card>
      )}

      <EncaissementModal
        open={encaissementOpen}
        onClose={() => setEncaissementOpen(false)}
        options={derived.options}
        presetEnrollmentId={presetEnrollmentId}
        onProvisional={setReceiptData}
        onRecorded={(receipt, balance) => {
          const enr = derived.enrollmentById.get(receipt.enrollmentId);
          const student = derived.studentById.get(receipt.studentId);
          if (enr && student) {
            setReceiptData(
              buildVersementReceipt({
                receipt,
                fees: balance.lines.map(toFeeSnapshot),
                student: receiptStudentOf(enr, student),
              }),
            );
          }
          reload();
        }}
      />

      <Modal
        open={toCancel !== null}
        onClose={() => setToCancel(null)}
        title="Annuler le reçu"
        subtitle={toCancel ? `${toCancel.receiptNo} — ${formatMoney(toCancel.total)}` : undefined}
        icon="ri-close-circle-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setToCancel(null)}>
              Retour
            </Button>
            <Button variant="danger" icon="ri-close-line" onClick={confirmCancel} disabled={cancelling}>
              {cancelling ? "Annulation..." : "Annuler le reçu"}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-foreground-600">
          Le reçu reste consultable mais ses montants sortent des totaux et sa recette est retirée des
          finances. Cette action est inscrite au journal.
        </p>
        <Textarea
          label="Motif"
          required
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Ex. erreur de montant, versement en double..."
        />
      </Modal>

      <StudentPaymentsModal
        open={detailEnr !== null && detailStudent !== null}
        onClose={() => setDetailId(null)}
        student={
          detailEnr && detailStudent ? receiptStudentOf(detailEnr, detailStudent) : { name: "", matricule: "", className: "", cycleLabel: "", schoolYear: "" }
        }
        fees={detailEnr ? feesOf(detailEnr.id) : []}
        receipts={detailEnr ? data.receipts.filter((v) => v.enrollmentId === detailEnr.id) : []}
        onCancelReceipt={
          can("payments:delete")
            ? (r) => {
                setCancelReason("");
                setToCancel(r);
              }
            : undefined
        }
        onEncaisser={() => {
          if (detailEnr) {
            const id = detailEnr.id;
            setDetailId(null);
            openEncaissement(id);
          }
        }}
        onPrintReceipt={setReceiptData}
      />

      <ReceiptPrintModal open={receiptData !== null} onClose={() => setReceiptData(null)} data={receiptData} school={school} />
    </div>
  );
}